import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const GYM_IMAGE = "https://images.unsplash.com/photo-1767531309781-b61fe1edfe86?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

const TEAL      = "#0891B2";
const TEAL_DARK = "#0E7490";
const NAVY      = "#0C2D48";

const BENEFITS = [
  { icon: "🤖", title: "AI-Powered Member Communication", desc: "Every call answered, every text replied — 24/7, 100% of the time. Live voice calls, two-way AI texting, and automated follow-ups. Once trained on your business, the AI responds exactly the way you would." },
  { icon: "📊", title: "All-in-One Platform", desc: "Members, classes, POS, trainer payroll, and analytics — one platform, zero chaos." },
  { icon: "💰", title: "60%+ Less Than the Competition", desc: "Mindbody and Glofox start at $500/month. GymPartnerAI gives you more for a fraction of the cost." },
  { icon: "🏋️", title: "Built by a Real Gym Owner", desc: "Every feature was built because a gym owner — Ron K. of Neighborhood Boxing Club — actually needed it. Not generic software adapted for gyms." },
];

/* ── Booking inline card ─────────────────────────────────────────────────── */
function BookingCard({ slots, loadingSlots, onSubmit, submitting, submitted }) {
  const [form, setForm] = useState({ first_name: "", gym_name: "", phone: "", slot: "" });
  if (submitted) return (
    <div style={{ background: "#EFF6FF", border: `2px solid ${TEAL}`, borderRadius: 16, padding: "32px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
      <h3 style={{ fontSize: 20, fontWeight: 700, color: NAVY, fontFamily: "'Poppins', sans-serif", textTransform: "none", letterSpacing: "normal" }}>Call Confirmed!</h3>
      <p style={{ color: "#475569" }}>Ron K. will call you at the time you selected. Check your texts for confirmation.</p>
    </div>
  );
  const ready = form.first_name && form.phone && form.slot;
  return (
    <div style={{ background: "#fff", border: `2px solid #A5F3FC`, borderRadius: 16, padding: "28px 24px", boxShadow: "0 4px 24px rgba(8,145,178,0.10)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, flexShrink: 0 }}>📞</div>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: 0, fontFamily: "'Poppins', sans-serif", textTransform: "none", letterSpacing: "normal", lineHeight: 1.35 }}>
              Book a 30-Minute Call<br />with Owner Ron K.
            </h3>
          <p style={{ fontSize: 13, color: "#64748B", margin: "2px 0 0" }}>No pressure. See the platform live before you decide anything.</p>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[{ key: "first_name", ph: "First Name", type: "text" },
          { key: "gym_name",   ph: "Gym / Studio Name", type: "text" },
          { key: "phone",      ph: "Phone Number",       type: "tel" }
        ].map(f => (
          <input key={f.key} type={f.type} placeholder={f.ph} value={form[f.key]}
            onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
            style={{ border: "1.5px solid #A5F3FC", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: NAVY, outline: "none", background: "#F0FDFF" }} />
        ))}
        <div>
          <label style={{ fontSize: 13, color: "#475569", fontWeight: 600, display: "block", marginBottom: 6 }}>Select a Time</label>
          {loadingSlots ? (
            <p style={{ color: "#94A3B8", fontSize: 13 }}>Loading available times…</p>
          ) : slots.length === 0 ? (
            <p style={{ color: "#94A3B8", fontSize: 13 }}>No slots available right now — check back soon.</p>
          ) : (
            <select value={form.slot} onChange={e => setForm(p => ({ ...p, slot: e.target.value }))}
              style={{ width: "100%", border: "1.5px solid #A5F3FC", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: NAVY, background: "#F0FDFF", outline: "none" }}>
              <option value="">Choose a time slot…</option>
              {slots.map(s => <option key={s.datetime_iso} value={s.datetime_iso}>{s.display}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => onSubmit({ ...form, source: "sales_page_inline" })}
          disabled={submitting || !ready}
          style={{ background: ready ? TEAL : "#A5F3FC", color: "#fff", border: "none", borderRadius: 10, padding: "14px 0", fontSize: 15, fontWeight: 900, cursor: ready ? "pointer" : "default", transition: "all 0.15s" }}>
          {submitting ? "Booking…" : "Confirm My 30-Minute Call →"}
        </button>
        <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", margin: 0 }}>By submitting you agree to receive SMS updates. Reply STOP to opt out.</p>
      </div>
    </div>
  );
}

/* ── Popup (2 steps) ─────────────────────────────────────────────────────── */
function Popup({ slots, loadingSlots, onClose, onLeadSubmit, onBookingSubmit, submitting }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ first_name: "", gym_name: "", phone: "" });
  const [slot, setSlot] = useState("");
  const [done, setDone] = useState(false);

  const step1Ready = form.first_name && form.phone;

  const handleStep1 = async () => {
    if (!step1Ready) return;
    await onLeadSubmit({ ...form, source: "popup" });
    setStep(2);
  };

  const handleStep2 = async () => {
    if (!slot) return;
    await onBookingSubmit({ ...form, slot_datetime: slot, source: "popup_step2" });
    setDone(true);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 20, maxWidth: 440, width: "100%", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        {/* Teal header */}
        <div style={{ background: `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL})`, padding: "20px 24px 16px", position: "relative" }}>
          <button onClick={onClose} style={{ position: "absolute", top: 12, right: 16, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 18, borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          {step === 1 && !done && <>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Cheaper Than Leading Brands</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#FCD34D", lineHeight: 1.1 }}>Not $499/month.</div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.9)", marginTop: 6, lineHeight: 1.5 }}>
              All-in-one AI gym management for only <strong style={{ color: "#FCD34D" }}>$199/month</strong>. First month FREE.
            </div>
          </>}
          {step === 2 && !done && <>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>📞 Schedule Your 30-Min Call</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginTop: 4 }}>Pick a time to speak with Ron K. directly.</div>
          </>}
          {done && <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>✅ Call Confirmed!</div>}
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          {done && (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: "#1E293B", fontSize: 15, marginBottom: 16 }}>Ron K. will call you at the confirmed time. You'll get a text confirmation shortly.</p>
              <button onClick={onClose} style={{ background: TEAL, color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Close</button>
            </div>
          )}
          {!done && step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ color: "#475569", fontSize: 13, margin: "0 0 4px" }}>Enter your info to claim your free first month. No credit card needed.</p>
              {[{ key: "first_name", ph: "First Name", type: "text" },
                { key: "gym_name",   ph: "Gym / Studio Name", type: "text" },
                { key: "phone",      ph: "Phone Number",       type: "tel" }
              ].map(f => (
                <input key={f.key} type={f.type} placeholder={f.ph} value={form[f.key]}
                  onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={{ border: "1.5px solid #A5F3FC", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: NAVY, outline: "none", background: "#F0FDFF" }} />
              ))}
              <button onClick={handleStep1} disabled={!step1Ready}
                style={{ background: step1Ready ? TEAL : "#A5F3FC", color: "#fff", border: "none", borderRadius: 10, padding: "14px 0", fontSize: 15, fontWeight: 900, cursor: step1Ready ? "pointer" : "default" }}>
                Get My Free Month →
              </button>
              <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", margin: 0 }}>Reply STOP to opt out of SMS.</p>
            </div>
          )}
          {!done && step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ color: "#475569", fontSize: 13, margin: "0 0 4px" }}>Now schedule your free 30-min call to see the platform live.</p>
              {loadingSlots ? (
                <p style={{ color: "#94A3B8", fontSize: 13 }}>Loading available times…</p>
              ) : slots.length === 0 ? (
                <p style={{ color: "#94A3B8", fontSize: 13 }}>No slots available right now. You can book on the page below.</p>
              ) : (
                <select value={slot} onChange={e => setSlot(e.target.value)}
                  style={{ width: "100%", border: "1.5px solid #A5F3FC", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: NAVY, background: "#F0FDFF", outline: "none" }}>
                  <option value="">Choose a time slot…</option>
                  {slots.map(s => <option key={s.datetime_iso} value={s.datetime_iso}>{s.display}</option>)}
                </select>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleStep2} disabled={!slot}
                  style={{ flex: 1, background: slot ? TEAL : "#A5F3FC", color: "#fff", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 900, cursor: slot ? "pointer" : "default" }}>
                  Confirm Call →
                </button>
                <button onClick={onClose}
                  style={{ flex: 1, background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  I'll Book Later
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function GymPartnerPage() {
  const [showPopup, setShowPopup]       = useState(false);
  const [slots, setSlots]               = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [bookingDone, setBookingDone]   = useState(false);

  useEffect(() => {
    // Load Poppins — clean, sharp, highly readable at all weights
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;900&display=swap";
    document.head.appendChild(link);

    document.title = "GymPartnerAI — Stop Manually Running Your Gym";
    const setMeta = (name, content, isProp) => {
      let el = document.querySelector(isProp ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); if (isProp) el.setAttribute("property", name); else el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "GymPartnerAI is an all-in-one AI gym management platform built by a real gym owner. Member management, AI texting, voice call answering, class scheduling, POS, and trainer payroll. More than 60% cheaper than Mindbody, Glofox, and Zen Planner.");
    setMeta("keywords",    "gym management software, gym software app, gym owner software, Mindbody alternative, Glofox alternative, Zen Planner alternative, WellnessLiving alternative, fitness studio software, boxing gym software, AI gym management, gym marketing automation, gym member management, gym class scheduling software, gym billing software, independent gym software, boutique fitness software, gym payroll software, gym AI assistant, gym texting software, gym voice answering");
    setMeta("og:title",       "GymPartnerAI — Stop Manually Running Your Gym", true);
    setMeta("og:description", "Built by a gym owner for gym owners. AI-powered, all-in-one. 60%+ cheaper than Mindbody and Glofox. First month free.", true);
    setMeta("og:type",        "website", true);
    setMeta("og:url",         "https://neighborhoodboxingclub.us/gympartnerai", true);
    setMeta("og:image",       "https://neighborhoodboxingclub.us/og-gympartnerai.jpg", true);
    setMeta("og:image:alt",   "GymPartnerAI — AI gym management software", true);
    setMeta("robots",         "index, follow");
    setMeta("canonical",      "https://neighborhoodboxingclub.us/gympartnerai");
    // JSON-LD schema — tells Google this is a SoftwareApplication
    let schema = document.querySelector('#gpa-schema');
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'gpa-schema';
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "GymPartnerAI",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-powered all-in-one gym management platform. Member management, AI texting, voice call answering, class scheduling, POS, and trainer payroll. Built by a real gym owner.",
      "offers": { "@type": "Offer", "price": "199", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "price": "199", "priceCurrency": "USD", "unitText": "MONTH" } },
      "url": "https://neighborhoodboxingclub.us/gympartnerai",
      "image": "https://neighborhoodboxingclub.us/og-gympartnerai.jpg",
      "author": { "@type": "Person", "name": "Ron K.", "jobTitle": "Gym Owner", "worksFor": { "@type": "Organization", "name": "Neighborhood Boxing Club" } }
    });

    const t = setTimeout(() => {
      if (!sessionStorage.getItem("gpa_popup_closed") && !sessionStorage.getItem("gpa_lead_captured")) {
        setShowPopup(true);
      }
    }, 4000);
    axios.get(`${API}/gympartner/availability`)
      .then(r => { setSlots(r.data.slots || []); setLoadingSlots(false); })
      .catch(() => setLoadingSlots(false));
    return () => clearTimeout(t);
  }, []);

  const handleLeadSubmit = async (data) => {
    try {
      await axios.post(`${API}/gympartner/lead`, data);
      sessionStorage.setItem("gpa_lead_captured", "1");
    } catch {}
  };

  const handleBookingSubmit = async (data) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/gympartner/booking`, {
        first_name:    data.first_name,
        gym_name:      data.gym_name,
        phone:         data.phone,
        slot_datetime: data.slot || data.slot_datetime,
        source:        data.source || "sales_page",
      });
      setBookingDone(true);
      sessionStorage.setItem("gpa_booked", "1");
    } catch (e) {
      alert(e.response?.data?.detail || "This slot was just taken — please choose another time.");
    }
    setSubmitting(false);
  };

  return (
    <div style={{ fontFamily: "'Poppins', 'Inter', -apple-system, sans-serif", background: "#fff" }}>
      {showPopup && (
        <Popup
          slots={slots}
          loadingSlots={loadingSlots}
          onClose={() => { setShowPopup(false); sessionStorage.setItem("gpa_popup_closed", "1"); }}
          onLeadSubmit={handleLeadSubmit}
          onBookingSubmit={handleBookingSubmit}
          submitting={submitting}
        />
      )}

      {/* ── NAV — minimal, just the logo ── */}
      <nav style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontWeight: 900, fontSize: 20, color: TEAL, letterSpacing: -0.5 }}>GymPartnerAI</div>
        <button onClick={() => document.getElementById("gpa-booking").scrollIntoView({ behavior: "smooth" })}
          style={{ background: TEAL, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
          Book a Call
        </button>
      </nav>

      {/* ── HERO — gym image with teal overlay, text on top ── */}
      <div style={{ position: "relative", minHeight: "90vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {/* Gym image */}
        <img src={GYM_IMAGE} alt="Modern gym" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        {/* Teal overlay — exactly like the screenshot */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(14, 116, 144, 0.62)" }} />

        {/* Hero content */}
        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 480, padding: "40px 24px 40px", textAlign: "center" }}>
          <h1 style={{ fontSize: "clamp(30px, 8vw, 50px)", fontWeight: 900, color: "#fff", lineHeight: 1.1, margin: "0 0 14px", letterSpacing: "-0.02em", textShadow: "0 2px 8px rgba(0,0,0,0.3)", fontFamily: "'Poppins', sans-serif", textTransform: "none" }}>
            Stop Manually Running Your Gym.
          </h1>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#FFB612", margin: "0 0 20px" }}>
            Gym Software Built by a Gym Owner, for Gym Owners.
          </p>
          <p style={{ fontSize: 16, color: "#fff", lineHeight: 1.7, margin: "0 0 28px", opacity: 0.95 }}>
            AI handles your leads, members manage themselves, trainers get paid automatically. You focus on coaching. Everything else runs itself.
          </p>
          <button
            onClick={() => document.getElementById("gpa-booking").scrollIntoView({ behavior: "smooth" })}
            style={{
              display: "block", width: "100%",
              background: TEAL, color: "#fff", border: "none",
              borderRadius: 10, padding: "17px 0",
              fontSize: 17, fontWeight: 900, cursor: "pointer",
              letterSpacing: 0.5, textTransform: "uppercase",
              boxShadow: "0 4px 20px rgba(8,145,178,0.35)",
              marginBottom: 16,
            }}
          >
            Book a Free 30-Min Call
          </button>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontStyle: "italic", margin: 0 }}>
            Running Neighborhood Boxing Club in Columbus, OH right now.
          </p>
        </div>
      </div>

      {/* ── BENEFITS ── */}
      <div style={{ background: "#F0FDFF", padding: "56px 20px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 700, color: NAVY, marginBottom: 8, fontFamily: "'Poppins', sans-serif", textTransform: "none", letterSpacing: "normal" }}>
            Everything Your Gym Needs.
          </h2>
          <p style={{ textAlign: "center", color: "#334155", fontSize: 15, marginBottom: 6, maxWidth: 460, margin: "0 auto 6px" }}>
            Built from the ground up by someone who actually runs one.
          </p>
          <p style={{ textAlign: "center", color: TEAL, fontSize: 14, fontWeight: 800, marginBottom: 40, margin: "0 auto 40px" }}>
            Ron K. — Owner, Neighborhood Boxing Club, Columbus OH
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 20 }}>
            {BENEFITS.map(b => (
              <div key={b.title} style={{ background: "#fff", borderRadius: 14, padding: "24px 20px", border: "1px solid #A5F3FC", boxShadow: "0 2px 12px rgba(8,145,178,0.08)" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{b.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 8, lineHeight: 1.4, fontFamily: "'Poppins', sans-serif", textTransform: "none", letterSpacing: "normal" }}>{b.title}</h3>
                <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.65, margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BOOKING SECTION ── */}
      <div id="gpa-booking" style={{ background: "#fff", padding: "56px 20px" }}>
        <div style={{ maxWidth: 500, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <h2 style={{ fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 700, color: NAVY, marginBottom: 8, fontFamily: "'Poppins', sans-serif", textTransform: "none", letterSpacing: "normal" }}>
              See It Live. Zero Commitment.
            </h2>
            <p style={{ color: "#64748B", fontSize: 15 }}>
              Book a free 30-minute call with Ron K. — the gym owner who built this — and see exactly how it works for your gym.
            </p>
          </div>
          <BookingCard
            slots={slots}
            loadingSlots={loadingSlots}
            onSubmit={handleBookingSubmit}
            submitting={submitting}
            submitted={bookingDone}
          />
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background: "#0C2D48", padding: "28px 20px", textAlign: "center" }}>
        <div style={{ fontWeight: 900, fontSize: 16, color: TEAL, marginBottom: 4 }}>GymPartnerAI</div>
        <p style={{ color: "#94A3B8", fontSize: 13, margin: "0 0 2px" }}>Powered by Neighborhood Boxing Club</p>
        <p style={{ color: "#64748B", fontSize: 12, margin: 0 }}>4324 N High St, Columbus, OH 43214</p>
      </div>
    </div>
  );
}
